import { useCallback, useEffect, useRef } from 'react'
import {
  extractRoundFromRealtimeDetail,
  isLotteryRoundClosed,
  normalizeRoundId,
  resolveRoundIdFromPayload,
  type LotteryRoundRealtimePayload,
} from './lotteryRoundRealtime'

type UseLotteryRoundCloseGuardOptions = {
  roundId?: string | null
  enabled?: boolean
  onRoundClosed: (roundDisplay: string) => void
  fetchRound?: (roundId: string) => Promise<LotteryRoundRealtimePayload | null>
  pollIntervalMs?: number
}

export function useLotteryRoundCloseGuard({
  roundId,
  enabled = true,
  onRoundClosed,
  fetchRound,
  pollIntervalMs = 15000,
}: UseLotteryRoundCloseGuardOptions): void {
  const roundIdRef = useRef(normalizeRoundId(roundId))
  const hasRedirectedRef = useRef(false)
  const onRoundClosedRef = useRef(onRoundClosed)
  onRoundClosedRef.current = onRoundClosed

  useEffect(() => {
    roundIdRef.current = normalizeRoundId(roundId)
    hasRedirectedRef.current = false
  }, [roundId])

  const handleClosedRound = useCallback((round: LotteryRoundRealtimePayload | null | undefined) => {
    if (!round || hasRedirectedRef.current) return
    const updatedRoundId = resolveRoundIdFromPayload(round)
    if (!updatedRoundId || updatedRoundId !== roundIdRef.current) return
    if (!isLotteryRoundClosed(round)) return

    hasRedirectedRef.current = true
    const roundDisplay = round.roundNumber ? `รอบที่ ${round.roundNumber}` : 'รอบนี้'
    onRoundClosedRef.current(roundDisplay)
  }, [])

  const handleRealtimeEvent = useCallback(
    (event?: Event) => {
      const detail = (event as CustomEvent | undefined)?.detail
      const content =
        detail && typeof detail === 'object' && 'content' in (detail as object)
          ? (detail as { content?: unknown }).content
          : detail
      const round =
        extractRoundFromRealtimeDetail(detail) ??
        (content && typeof content === 'object'
          ? extractRoundFromRealtimeDetail({ round: (content as { round?: unknown }).round ?? content })
          : null)
      handleClosedRound(round)
    },
    [handleClosedRound],
  )

  useEffect(() => {
    if (!enabled || !roundIdRef.current) return

    window.addEventListener('lottery_round_updated', handleRealtimeEvent)
    window.addEventListener('lottery_round_update', handleRealtimeEvent)
    window.addEventListener('content_update', handleRealtimeEvent)

    return () => {
      window.removeEventListener('lottery_round_updated', handleRealtimeEvent)
      window.removeEventListener('lottery_round_update', handleRealtimeEvent)
      window.removeEventListener('content_update', handleRealtimeEvent)
    }
  }, [enabled, handleRealtimeEvent, roundId])

  useEffect(() => {
    if (!enabled || !roundIdRef.current || !fetchRound) return

    const checkRoundStatus = async () => {
      if (hasRedirectedRef.current) return
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      try {
        const currentRound = await fetchRound(roundIdRef.current)
        handleClosedRound(currentRound)
      } catch {
        // ignore polling errors
      }
    }

    const intervalId = window.setInterval(checkRoundStatus, pollIntervalMs)
    return () => window.clearInterval(intervalId)
  }, [enabled, fetchRound, handleClosedRound, pollIntervalMs, roundId])
}
